import {
  adjust,
  always,
  applySpec,
  evolve,
  findIndex,
  identity,
  pick,
  pipe,
  propEq,
  reject,
} from "ramda";
import { useCallback, useMemo, useReducer, useRef } from "react";

const ACTION = {
  UPDATE: "UPDATE",
  DESTROY: "DESTROY",
};

const INITIAL_STATE = {
  stack: [],
};

const adjustStack = (payload) => (stack) => {
  const index = findIndex(propEq(payload.id, "id"))(stack);
  if (index === -1) return [...stack, payload];
  else return adjust(index, (item) => ({ ...item, ...payload }), stack);
};

const reducer = (state, { payload, type }) => {
  switch (type) {
    case ACTION.UPDATE:
      return evolve({ stack: adjustStack(payload) }, state);
    case ACTION.DESTROY:
      return evolve({ stack: reject(propEq(payload.id, "id")) }, state);
    default:
      return state;
  }
};

export const useStackManager = ({ mapExtraProps } = {}) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { stack } = state;

  const mapProps = useCallback(
    ({ onClose: onCloseProp, ...item }) => {
      const onClose = () => {
        dispatch({
          type: ACTION.UPDATE,
          payload: { id: item.id, open: false },
        });
        onCloseProp?.();
      };
      return {
        ...item,
        open: item.open ?? false,
        key: item.id,
        onClose,
        ...mapExtraProps?.(item, { onClose }),
      };
    },
    [mapExtraProps],
  );

  const stackRef = useRef(stack);
  stackRef.current = stack;

  const ctx = useMemo(
    () => ({
      updateItem: pipe(
        applySpec({ type: always(ACTION.UPDATE), payload: identity }),
        dispatch,
      ),
      destroyItem: pipe(
        applySpec({
          type: always(ACTION.DESTROY),
          payload: pick(["id"]),
        }),
        dispatch,
      ),
    }),
    [],
  );

  return { stack, ctx, mapProps };
};
